import { camelize, snakeize } from 'casing'
import { AnyAction, Dispatch } from 'redux'
import {
  fetchSellerData,
  fetchSellerDataMicrosoft,
  patchSellersData,
  postMigrationSyncData,
} from '../../../flyout/api/markeplace'
import { getErrorMessages } from '../../../common/utils/error'
import {
  DefaultSuccesrOnSubmitData,
  ListingTypeUpdationUnsuccessful,
  RequestFailureMessage,
  WaitForMigrationSync,
} from '../../../common/utils/messagesContants'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import {
  startLoading,
  stopLoading,
} from '../../../common/modules/loading/actions'
import { LoadingTypes } from '../../../common/modules/loading/reducer'
import { errorLogger } from '../../../common/utils/errorLogger'
import {
  FlyOutListingData,
  FlyOutListingUserType,
  MicrosoftFlyOutListingData,
} from './reducer'
import { capitalize } from 'common/utils/vanilla'
import { AppDispatch, RootState } from '../../../store'
import { AxiosError, AxiosResponse } from 'axios'

export enum FlyOutSyncsActionTypes {
  SET_FLYOUT_LISTING_DATA = 'SET_FLYOUT_LISTING_DATA',
  SET_FLYOUT_LISTING_DATA_MICROSOFT = 'SET_FLYOUT_LISTING_DATA_MICROSOFT',
}

export const getFlyoutListingData =
  ({
    partnerId,
    metadata = false,
  }: {
    partnerId: string
    metadata?: boolean
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO))
    try {
      const { data } = await fetchSellerData({
        partnerId,
        metadata: { metadata },
      })
      const camelizedData = camelize(data)
      // Transform 'type' from backend to 'sellerType' for frontend
      const transformedData = {
        ...camelizedData,
        ...(camelizedData.type ? { sellerType: camelizedData.type } : {}),
      }
      delete transformedData.type
      await dispatch(setListingData(transformedData))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO))
    }
  }

export const getFlyoutListingDataMicrosoft =
  ({ accountId }: { accountId: string }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.MARKETPLACE_INFO_MICROSOFT))
    try {
      const { data } = await fetchSellerDataMicrosoft({
        accountId,
      })
      await dispatch(setListingDataMicrosoft(camelize(data)))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.MARKETPLACE_INFO_MICROSOFT))
    }
  }

export const sendListingType =
  (
    partnerId: string,
    listingType: FlyOutListingUserType,
    cloudMarketplace = 'aws',
    operations = 'update_partner_details'
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { data } = await patchSellersData(
        partnerId,
        { origin: listingType },
        cloudMarketplace,
        operations
      )
      const camelizedData = camelize(data)
      if (camelizedData.updatePartnerDetails.updated) {
        await dispatch(setListingData({ origin: listingType }))
      } else {
        await dispatch(
          updateAppAlert({
            message: ListingTypeUpdationUnsuccessful,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        await dispatch(
          getFlyoutListingData({
            partnerId,
            metadata: false,
          }) as unknown as AnyAction
        )
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const patchSellerData =
  (
    partnerId: string,
    sellerData: FlyOutListingData,
    cloudMarketplace = 'aws',
    operations = 'update_partner_details'
  ) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      // Transform 'sellerType' to 'type' for backend
      const { sellerType, ...restData } = sellerData
      const dataToSend = {
        ...restData,
        ...(sellerType ? { type: sellerType } : {}),
      }
      const { data } = await patchSellersData(
        partnerId,
        snakeize(dataToSend),
        cloudMarketplace,
        operations
      )
      const camelizedData = camelize(data)
      if (camelizedData.updatePartnerDetails.updated) {
        await dispatch(setListingData(sellerData))
        await dispatch(
          updateAppAlert({
            message: DefaultSuccesrOnSubmitData,
            messageType: 'SUCCESS',
            autoClose: true,
          })
        )
      } else {
        await dispatch(
          updateAppAlert({
            message: ListingTypeUpdationUnsuccessful,
            messageType: 'ERROR',
            autoClose: true,
          })
        )
        await dispatch(
          getFlyoutListingData({
            partnerId,
            metadata: false,
          }) as unknown as AnyAction
        )
      }
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
export const setListingData = (
  listingData: Record<string, unknown> | FlyOutListingData
) => ({
  type: FlyOutSyncsActionTypes.SET_FLYOUT_LISTING_DATA,
  payload: listingData,
})
export type EntityType = 'offers' | 'agreements'
export const sendSync =
  (partnerId: string, entity: EntityType) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    await dispatch(
      updateAppAlert({
        message: WaitForMigrationSync,
        messageType: 'WARNING',
        autoClose: false,
      })
    )
    try {
      await postMigrationSyncData(partnerId, { entity })
      await dispatch(
        updateAppAlert({
          message: `${capitalize(entity)} have been synced successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setListingDataMicrosoft = (
  listingDataMicrosoft: MicrosoftFlyOutListingData
) => ({
  type: FlyOutSyncsActionTypes.SET_FLYOUT_LISTING_DATA_MICROSOFT,
  payload: listingDataMicrosoft,
})
